<template>
  <div class="row">
    <div ref="products" class="routine-products">
      <carousel
        :per-page="perPage"
        :carousel-identifier="carouselIdentifier"
        :navigation-enabled="enableNavigation"
      >
        <slide
          v-for="(slot, index) in getSlots()"
          :key="index"
          :style="`--slider-index: ${-sliderOffset}px`"
        >
          <slot :name="`product${slot}`"></slot>
        </slide>
      </carousel>
    </div>
  </div>
</template>

<script>
const DEFAULT_PER_PAGE = 1;

import throttle from 'lodash/throttle';

export default {
  name: 'routine-products',
  data() {
    return {
      perPage: this.getPerPage(),
      slides: false,
      currentSlide: 0,
      taggingInfo: [],
      scrollCallbackReference: null,
      eventTriggeredItems: [],
      sliderOffset: 0,
      sliderWidth: 0,
    };
  },

  props: {
    carouselIdentifier: { type: String, required: false, default: `carousel-${Date.now()}` },
    enableNavigation: { type: Boolean, default: false },
  },

  mounted() {
    this.perPage = this.getPerPage();
    this.slides = this.$el.querySelectorAll('.carousel-slide');
    if (window.eventBus) {
      window.eventBus.$on('mediaquery.changed', () => {
        this.perPage = this.getPerPage();
        this.fireProductImpression(this.currentSlide);
      });
      window.eventBus.$on(`${this.carouselIdentifier}-pageChange`, this.onPageChange);
    }

    // check if element is in window
    /* instanbul ignore next */
    this.scrollCallbackReference = throttle(() => {
      this.fireProductImpression(this.currentSlide);
    }, 500);

    /* instanbul ignore next */
    window.addEventListener('scroll', this.scrollCallbackReference);
    this.removelozyloaded(this.perPage);
  },
  // watch: {
  //   sliderWidth: {
  //     handler: function (latest) {
  //       this.sliderOffset = latest
  //     },
  //     immediate: true,
  //   }
  // },
  methods: {
    getSlots() {
      return Object.keys(this.$slots).length;
    },

    getPerPage() {
      let perPage = DEFAULT_PER_PAGE;

      /* istanbul ignore next */
      if (window.ScreenSizeHandler) {
        if (window.ScreenSizeHandler.getMqForSize('small').selected) {
          perPage = 1;
        }

        if (window.ScreenSizeHandler.getMqForSize('medium').selected) {
          perPage = 2;
        }

        if (window.ScreenSizeHandler.getMqForSize('large').selected) {
          perPage = 3;
        }
      }
      window.eventBus.$emit('perPage', perPage);
      return perPage;
    },

    fireProductImpression(currentSlide) {
      // NOSONAR
      if (!this.isElementInViewport(this.$refs.products)) {
        return;
      }
      this.currentSlide = currentSlide;
      this.sliderOffset = this.$children.length > 0 ? this.$children[0].currentOffset : 0;
      if (this.perPage === 1) {
        const param = !this.eventTriggeredItems.includes(this.currentSlide)
          ? this.slides[this.currentSlide]
          : '';
        if (param) {
          this.eventTriggeredItems.push(this.currentSlide);
        }
        this.taggingInfo = this.getTaggingInfoForSlides(param);
      }

      if (this.perPage === 2) {
        const param1 = !this.eventTriggeredItems.includes(this.currentSlide)
          ? this.slides[this.currentSlide]
          : '';
        const param2 = !this.eventTriggeredItems.includes(this.currentSlide + 1)
          ? this.slides[this.currentSlide + 1]
          : '';
        if (param1) {
          this.eventTriggeredItems.push(this.currentSlide);
        }
        if (param2) {
          this.eventTriggeredItems.push(this.currentSlide + 1);
        }
        this.taggingInfo = this.getTaggingInfoForSlides([param1, param2]);
      }

      if (this.perPage === 3) {
        const param1 = !this.eventTriggeredItems.includes(this.currentSlide)
          ? this.slides[this.currentSlide]
          : '';
        const param2 = !this.eventTriggeredItems.includes(this.currentSlide + 1)
          ? this.slides[this.currentSlide + 1]
          : '';
        const param3 = !this.eventTriggeredItems.includes(this.currentSlide + 2)
          ? this.slides[this.currentSlide + 2]
          : '';
        if (param1) {
          this.eventTriggeredItems.push(this.currentSlide);
        }
        if (param2) {
          this.eventTriggeredItems.push(this.currentSlide + 1);
        }
        if (param3) {
          this.eventTriggeredItems.push(this.currentSlide + 2);
        }
        this.taggingInfo = this.getTaggingInfoForSlides([param1, param2, param3]);
      }

      if (this.scrollCallbackReference) {
        window.removeEventListener('scroll', this.scrollCallbackReference);
      }
      this.triggerTaggingEvent(this.taggingInfo);
    },
    triggerTaggingEvent(taggingItems) {
      const impressions = [];
      taggingItems.forEach((taggingItem) => {
        const { ecommerce } = taggingItem;
        const productTaggingDetails = ecommerce.impressions[0];
        impressions.push({
          price: productTaggingDetails.price || 'none',
          brand: productTaggingDetails.brand,
          category: productTaggingDetails.category,
          id: productTaggingDetails.id,
          list: productTaggingDetails.list,
          name: productTaggingDetails.name,
          position: productTaggingDetails.position,
          variant: decodeURI(productTaggingDetails.variant),
          dimension35: decodeURI(productTaggingDetails.dimension35),
          dimension36: decodeURI(productTaggingDetails.dimension36),
          dimension38: productTaggingDetails.dimension38,
          dimension48: productTaggingDetails.dimension48,
          dimension59: productTaggingDetails.dimension59,
          dimension91: productTaggingDetails.dimension91,
        });
      });
      const { eventLabel, eventCategory } = taggingItems[0];
      const currencyCode = taggingItems[0].ecommerce.currencyCode || 'none';
      try {
          dataLayer.push({ //eslint-disable-line
          event: 'nievent',
          event_name: 'view_item_list',
          eventCategory: eventCategory,
          eventAction: 'Product Impressions',
          eventLabel: eventLabel,
          ecommerce: {
            currencyCode: currencyCode,
            impressions: [],
            product: impressions,
          },
        });
      } catch {
        console.warn('datalayer is not defined');
      }
    },
    getTaggingInfoForSlides(nodesAsArray) {
      const data = [];
      if (this.perPage === 1 && nodesAsArray) {
        data.push(this.getTaggingDataForNode(nodesAsArray));
      } else {
        nodesAsArray.forEach((node) => {
          if (node) {
            data.push(this.getTaggingDataForNode(node));
          }
        });
      }
      return data;
    },
    getTaggingDataForNode(node) {
      const taggingData = node
        .querySelector('[data-tagging-on-impression]')
        .getAttribute('data-tagging-on-impression');
      return JSON.parse(taggingData) || false;
    },
    onPageChange(currentSlide) {
      /* istanbul ignore next */
      this.fireProductImpression(currentSlide);
      try {
        dataLayer.push({
          event: 'uaevent',
          eventCategory: 'routinefinder',
          eventAction: 'display',
          eventLabel: currentSlide + 1,
          ecommerce: undefined,
        });
      } catch {
        console.warn('datalayer is not defined');
      }
    },
    isElementInViewport(element) {
      const rect = element.getBoundingClientRect();
      const elementCenter = {
        x: rect.left + element.offsetWidth / 2,
        y: rect.top + element.offsetHeight / 2,
      };
      let pointContainer = document.elementFromPoint(elementCenter.x, elementCenter.y);
      if (
        elementCenter.x < 0 ||
        elementCenter.y < 0 ||
        elementCenter.x > (document.documentElement.clientWidth || window.innerWidth) ||
        elementCenter.y > (document.documentElement.clientHeight || window.innerHeight)
      ) {
        return false;
      }

      do {
        if (pointContainer === element) {
          return true;
        }
      } while ((pointContainer = pointContainer.parentNode));

      return false;
    },
    removelozyloaded(perPage) {
      // remove lazyloaded class
      const a = document.getElementsByClassName('routine-products')[0];
      // a.classList.add('slide')
      const b = a.querySelectorAll('.carousel-slide img');
      if (b.length > perPage) {
        for (let i = perPage; i < b.length; i++) {
          b[i].classList.remove('lazyload');
        }
      }
      //  end
    },
  },
};
</script>
